/* eslint-disable eqeqeq */
import React, { ReactNode } from "react";
import { Table, Input, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { GenericOrder, GetActiveOrders } from "../services/OrderService";
import { Capitalize, ConvertDateTime, ConvertOrderLocation } from "../helper";
import OrderDetails from "../components/OrderDetails";
import ExcelJS from "exceljs";

import "dayjs/locale/tr";
import dayjs from "dayjs";
dayjs.locale("tr");

const getBase64FromUrl = async (url) => {
  const data = await fetch(url);
  const blob = await data.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result;
      resolve(base64data);
    };
  });
};

function getMachineQrValue(order) {
  const clean = (value) => {
    if (value === undefined || value === null) return "";
    return String(value).trim();
  };

  const join = (parts, separator = " ") =>
    parts.map(clean).filter(Boolean).join(separator);

  const product =
    order.brand == "turktraktor"
      ? join([order.brand, order.model || order.description])
      : order.production_unit == "turbo"
        ? join([
            order.brand,
            clean(order.tank_size) ? clean(order.tank_size) + "LT" : "",
            order.cooler_type,
            clean(order.fan_radius) ? clean(order.fan_radius) + "cm" : "",
            order.fan_type,
          ])
        : order.production_unit == "kollu"
          ? join([
              order.brand,
              clean(order.tank_size) ? clean(order.tank_size) + "LT" : "",
              order.arm_type,
              order.arm_length,
            ])
          : order.production_unit == "diger"
            ? join([order.brand, order.type])
            : "";

  return (
    order.serial_number +
    "," +
    (product || clean(order.description)).toUpperCase()
  );
}

async function exportWithExcel(data) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Aktif Makineler");
  sheet.properties.defaultRowHeight = 80;

  sheet.columns = [
    { header: "Seri No", key: "serial_number", width: 18 },
    { header: "Sipariş No", key: "order_no", width: 18 },
    { header: "Sipariş Tarihi", key: "date", width: 22 },
    { header: "Müşteri", key: "customer", width: 32 },
    { header: "Telefon", key: "phone_number", width: 18 },
    { header: "Adres", key: "address", width: 48 },
    { header: "Sipariş Alan", key: "order_tooker", width: 22 },
    { header: "Durum", key: "location", width: 24 },
    { header: "Üretim Birimi", key: "production_unit", width: 18 },
    { header: "Son İşlem Tarihi", key: "last_location_date", width: 24 },
    { header: "Sipariş Özellikleri", key: "order_details", width: 64 },
    { header: "QR", key: "qr_code", width: 18 },
  ];

  await Promise.all(
    data?.map(async (order) => {
      const row = sheet.addRow({
        serial_number: order.serial_number,
        order_no: order.order_no,
        date: order.date,
        customer: order.customer,
        phone_number: order.phone_number,
        address: order.address,
        order_tooker: order.order_tooker,
        location: order.location,
        production_unit: order.production_unit,
        last_location_date: ConvertDateTime(order.last_location_date),
        order_details: [
          order.brand,
          order.production_unit,
          order.caldron_color,
          order.caldron_type,
          order.cooler_type,
          order.fan_radius,
          order.fan_type,
          order.pump,
          order.meme_distance,
          order.meme_type,
          order.tank_size,
          order.type,
          order.tow_type,
          order.alt_features,
          order.year,
        ]
          .filter(Boolean)
          .join(" / "),
      });

      const qrCodeValue = getMachineQrValue(order);
      const url = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
        qrCodeValue
      )}`;
      const res = await getBase64FromUrl(url);

      const imageId = workbook.addImage({
        base64: res,
        extension: "jpg",
      });

      sheet.addImage(imageId, {
        tl: { col: 11, row: row.number - 1 },
        ext: { width: 100, height: 100 },
      });
    })
  );

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "aktif-makineler.xlsx";
  anchor.click();
  window.URL.revokeObjectURL(url);
}

export default class ActiveOrdersScreen extends React.Component {
  configFile = JSON.parse(localStorage.getItem("config") ?? "{}");

  getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node: ReactNode) => {
            this.searchInput = node;
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            this.handleSearch(selectedKeys, confirm, dataIndex)
          }
          style={{
            width: 188,
            marginBottom: 8,
            display: "block",
          }}
        />
        <Button
          type="primary"
          onClick={() => this.handleSearch(selectedKeys, confirm, dataIndex)}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Bul
        </Button>
        <Button
          onClick={() => this.handleReset(clearFilters)}
          size="small"
          style={{ width: 90 }}
        >
          Sıfırla
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        type="search"
        style={{ color: filtered ? "#1890ff" : undefined }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        ?.toLowerCase()
        ?.includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => this.searchInput.select());
      }
    },
    render: (text) => text,
  });

  handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    this.setState({
      searchText: selectedKeys[0],
      searchedColumn: dataIndex,
    });
  };

  handleReset = (clearFilters) => {
    clearFilters();
    this.setState({ searchText: "" });
  };

  state = {
    csvdata: [
      [
        "Seri No",
        "Sipariş No",
        "Sipariş Tarihi",
        "Müşteri",
        "Telefon",
        "Adres",
        "Sipariş Alan",
        "Sipariş Özellikleri",
        "Durum",
        "Üretim Birimi",
      ],
    ],
    movableMachines: [],
    orderModalVisible: false,
    orderDetails: {},
    exportLoading: false,
    loading: false,
    size: "small",
    top: "none",
    bottom: "bottomRight",
    ellipsis: true,
    data: [],
    columns: [
      {
        title: "Sipariş No",
        dataIndex: "order_no",
        sorter: (a: GenericOrder, b: GenericOrder) =>
          a.order_no.localeCompare(b.order_no),
        ...this.getColumnSearchProps("order_no"),
      },
      {
        title: "Müşteri",
        dataIndex: "customer",
        sorter: (a: GenericOrder, b: GenericOrder) =>
          a.customer.localeCompare(b.customer),
        ...this.getColumnSearchProps("customer"),
      },
      {
        title: "Telefon No",
        dataIndex: "phone_number",
        ...this.getColumnSearchProps("phone_number"),
      },
      {
        title: "Adres",
        dataIndex: "address",
        ...this.getColumnSearchProps("address"),
      },
      {
        title: "Seri No",
        dataIndex: "serial_number",
        sorter: (a: GenericOrder, b: GenericOrder) =>
          a.serial_number.localeCompare(b.serial_number),
        ...this.getColumnSearchProps("serial_number"),
      },
      {
        title: "Ürün Tipi",
        dataIndex: "production_unit",
        render: (text: string) => <td>{Capitalize(text)}</td>,
        sorter: (a: GenericOrder, b: GenericOrder) =>
          a.production_unit.localeCompare(b.production_unit),
      },
      {
        title: "Son İşlem Tarihi",
        dataIndex: "last_location_date",
        defaultSortOrder: "descend",
        render: (text: string) => <td>{ConvertDateTime(text)}</td>,
        sorter: (a: GenericOrder, b: GenericOrder) =>
          dayjs(a.last_location_date).unix() -
          dayjs(b.last_location_date).unix(),
      },
      {
        title: "Bulunduğu Konum",
        dataIndex: "location",
        sorter: (a: GenericOrder, b: GenericOrder) =>
          a.location.localeCompare(b.location),
        ...this.getColumnSearchProps("location"),
      },
    ],
  };

  componentDidMount = () => {
    let csvdata = [
      [
        "Seri No",
        "Sipariş No",
        "Sipariş Tarihi",
        "Müşteri",
        "Telefon",
        "Adres",
        "Sipariş Alan",
        "Durum",
        "Üretim Birimi",
        "Sipariş Özellikleri",
      ],
    ];
    GetActiveOrders().then((res) => {
      let filteredRes = res?.filter(
        (detail) =>
          !(
            detail.location == "iptal" ||
            detail.location == "teslimat" ||
            detail.location == "onaybekliyor"
          )
      );
      filteredRes = filteredRes?.map((detail) => {
        detail.location = ConvertOrderLocation(detail.location);
        return detail;
      });

      this.setState({ data: filteredRes, csvDataloaded: true });
      this.state.data?.forEach((order) => {
        let order_details =
          order.brand +
          "$" +
          order.production_unit +
          "$" +
          order.caldron_color +
          "$" +
          order.caldron_type +
          "$" +
          order.cooler_type +
          "$" +
          order.fan_radius +
          "$" +
          order.fan_type +
          "$" +
          order.pump +
          "$" +
          order.meme_distance +
          "$" +
          order.meme_type +
          "$" +
          order.tank_size +
          "$" +
          order.type +
          "$" +
          order.tow_type +
          "$" +
          order.alt_features +
          "$" +
          order.year;
        csvdata.push([
          order.serial_number,
          order.order_no,
          order.date,
          order.customer,
          order.phone_number,
          order.address,
          order.order_tooker,
          order.location,
          order.production_unit,
          order_details,
        ]);
      });
      this.setState({ csvdata });
    });
  };

  searchInput: React.ReactNode;

  exportActiveOrders = async () => {
    this.setState({ exportLoading: true });
    try {
      await exportWithExcel(this.state.data);
    } finally {
      this.setState({ exportLoading: false });
    }
  };

  render() {
    const state = this.state;

    return (
      <>
        <Table
          rowKey={(row) => "key" + row.last_location_date + row.serial_number}
          size="small"
          loading={this.state.loading}
          pagination={{ defaultPageSize: 10, position: ["bottomRight"] }}
          columns={this.state.columns}
          dataSource={state.data}
          expandable={{
            expandedRowRender: (record) => (
              <OrderDetails serial_number={record.serial_number} />
            ),
          }}
        />
        <Button
          loading={this.state.exportLoading}
          style={{ float: "right", marginRight: 10, marginTop: 10 }}
          type="primary"
          onClick={this.exportActiveOrders}
        >
          Excel e aktar
        </Button>
      </>
    );
  }
}
